import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, ChevronRight, Lock, CheckCircle, PenLine, Printer } from 'lucide-react';
import { useCourse } from '../hooks/useCourse';
import { useAuth } from '../hooks/useAuth';
import { logActivity, markLessonComplete, supabase } from '../lib/supabase';
import { TextLesson, LessonSection } from '../types';
import { lessonOrder as deutschLessonOrder } from '../data/deutsch-schularbeit/fill-in-questions';
import { lessonOrder as matheLessonOrder } from '../data/mathe-schularbeit/fill-in-questions';

function LessonReadButton({ userId, courseId, lessonId, onMarked, onReadStateChange }: {
  userId?: string; courseId: string; lessonId: string; onMarked: () => void; onReadStateChange?: (isRead: boolean) => void;
}) {
  const [isRead, setIsRead] = useState(false);
  const [checking, setChecking] = useState(true);
  const [step, setStep] = useState<'initial' | 'confirm'>('initial');

  useEffect(() => {
    if (!userId || !courseId || !lessonId) { setChecking(false); return; }
    supabase
      .from('lesson_progress')
      .select('completed')
      .eq('user_id', userId)
      .eq('course_id', courseId)
      .eq('lesson_id', lessonId)
      .then(({ data }) => {
        const read = data?.some(r => r.completed) || false;
        setIsRead(read);
        setChecking(false);
        if (onReadStateChange) onReadStateChange(read);
      });
  }, [userId, courseId, lessonId, onReadStateChange]);

  // Reset step when lesson changes
  useEffect(() => { setStep('initial'); }, [lessonId]);

  if (checking) return null;

  if (isRead) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: '8px', padding: '12px', marginTop: '24px', color: '#4caf50', fontSize: '14px',
      }}>
        <CheckCircle size={16} /> Lektion als gelesen markiert
      </div>
    );
  }

  // Step 1: "Alles gelesen"
  if (step === 'initial') {
    return (
      <button
        onClick={() => setStep('confirm')}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '8px', padding: '14px', marginTop: '24px',
          background: 'rgba(255, 213, 79, 0.1)', border: '1px solid rgba(255, 213, 79, 0.3)',
          borderRadius: '12px', color: '#ffd54f', fontSize: '15px', fontWeight: 600, cursor: 'pointer',
        }}
      >
        📖 Alles gelesen
      </button>
    );
  }

  // Step 2: "Wirklich verstanden?"
  return (
    <div style={{
      marginTop: '24px',
      background: 'rgba(255, 213, 79, 0.08)',
      border: '1px solid rgba(255, 213, 79, 0.2)',
      borderRadius: '14px',
      padding: '16px',
      textAlign: 'center',
    }}>
      <div style={{
        color: '#ffd54f', fontSize: '16px', fontWeight: 600, marginBottom: '14px',
      }}>
        Hast du auch alles verstanden? 🤔
      </div>
      <div style={{ display: 'flex', gap: '10px' }}>
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '6px', padding: '14px',
            background: 'rgba(255, 255, 255, 0.06)', border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: '10px', color: '#e8eaf6', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
          }}
        >
          📖 Nochmal lesen
        </button>
        <button
          onClick={async () => {
            if (userId && courseId && lessonId) {
              await markLessonComplete(userId, courseId, lessonId);
              setIsRead(true);
              if (onReadStateChange) onReadStateChange(true);
              onMarked();
            }
          }}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '6px', padding: '14px',
            background: 'rgba(76, 175, 80, 0.15)', border: '1px solid rgba(76, 175, 80, 0.3)',
            borderRadius: '10px', color: '#4caf50', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
          }}
        >
          ✅ Ja, verstanden
        </button>
      </div>
    </div>
  );
}

function SectionRenderer({ section }: { section: LessonSection }) {
  switch (section.type) {
    case 'heading':
      return (
        <h3 style={{
          color: '#ffd54f',
          fontSize: '18px',
          fontWeight: 700,
          marginTop: '24px',
          marginBottom: '12px',
        }}>
          {section.content}
        </h3>
      );

    case 'rule':
      return (
        <div style={{
          background: 'rgba(156, 39, 176, 0.15)',
          borderLeft: '4px solid #9c27b0',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '12px',
        }}>
          <div style={{
            color: '#e8eaf6',
            fontSize: '15px',
            lineHeight: '1.7',
            whiteSpace: 'pre-line',
          }}>
            {section.content}
          </div>
        </div>
      );

    case 'example':
      return (
        <div style={{
          background: 'rgba(76, 175, 80, 0.1)',
          borderRadius: '10px',
          padding: '12px 16px',
          marginBottom: '8px',
          fontSize: '15px',
          color: '#e8eaf6',
          lineHeight: '1.5',
        }}>
          {section.content}
        </div>
      );

    case 'table':
      return (
        <div style={{
          overflowX: 'auto',
          marginBottom: '16px',
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px',
          }}>
            {section.headers && (
              <thead>
                <tr>
                  {section.headers.map((header, i) => (
                    <th key={i} style={{
                      background: 'rgba(66, 165, 245, 0.2)',
                      color: '#42a5f5',
                      padding: '10px 12px',
                      textAlign: 'left',
                      fontWeight: 700,
                      borderBottom: '2px solid rgba(66, 165, 245, 0.3)',
                    }}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            {section.rows && (
              <tbody>
                {section.rows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j} style={{
                        padding: '10px 12px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        color: '#e8eaf6',
                      }}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
      );

    case 'tip':
      return (
        <div style={{
          background: 'rgba(255, 213, 79, 0.1)',
          border: '1px solid rgba(255, 213, 79, 0.3)',
          borderRadius: '12px',
          padding: '14px 16px',
          marginBottom: '12px',
          fontSize: '14px',
          color: '#ffd54f',
          lineHeight: '1.6',
        }}>
          {section.content}
        </div>
      );

    default:
      return null;
  }
}

interface LessonUnlockStatus {
  lessonId: string;
  isUnlocked: boolean;
  fillInScore: number | null;
  fillInPassed: boolean;
}

function LessonCard({ lesson, onClick, isActive, unlockStatus, isDeutschCourse }: {
  lesson: TextLesson;
  onClick: () => void;
  isActive: boolean;
  unlockStatus?: LessonUnlockStatus;
  isDeutschCourse: boolean;
}) {
  const isLocked = false; // All lessons accessible — user decides what to practice
  const hasPassed = unlockStatus?.fillInPassed;

  return (
    <button
      onClick={isLocked ? undefined : onClick}
      disabled={isLocked}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        padding: '16px',
        background: isLocked
          ? 'rgba(255, 255, 255, 0.02)'
          : isActive
            ? 'rgba(255, 213, 79, 0.15)'
            : 'rgba(255, 255, 255, 0.05)',
        border: isActive
          ? '2px solid #ffd54f'
          : isLocked
            ? '1px solid rgba(255, 255, 255, 0.05)'
            : '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        cursor: isLocked ? 'not-allowed' : 'pointer',
        textAlign: 'left',
        marginBottom: '8px',
        transition: 'all 0.2s ease',
        opacity: isLocked ? 0.5 : 1,
      }}
    >
      <span style={{ fontSize: '24px', filter: isLocked ? 'grayscale(1)' : 'none' }}>
        {lesson.icon}
      </span>
      <div style={{ flex: 1 }}>
        <div style={{
          color: isLocked ? 'rgba(255,255,255,0.4)' : isActive ? '#ffd54f' : '#e8eaf6',
          fontSize: '15px',
          fontWeight: 600,
        }}>
          {lesson.title}
        </div>
        {isDeutschCourse && unlockStatus && (
          <div style={{
            fontSize: '12px',
            marginTop: '4px',
            color: hasPassed ? '#4caf50' : isLocked ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.5)',
          }}>
            {hasPassed ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <CheckCircle size={12} />
                Übung abgeschlossen
              </span>
            ) : isLocked ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Lock size={12} />
                Vorherige Übung abschließen
              </span>
            ) : (
              'Übung noch offen'
            )}
          </div>
        )}
      </div>
      {isLocked ? (
        <Lock size={20} style={{ color: 'rgba(255,255,255,0.3)' }} />
      ) : hasPassed ? (
        <CheckCircle size={20} style={{ color: '#4caf50' }} />
      ) : (
        <ChevronRight size={20} style={{ color: isActive ? '#ffd54f' : 'rgba(255,255,255,0.4)' }} />
      )}
    </button>
  );
}

export function TextLessonView() {
  const { courseId } = useParams<{ courseId: string }>();
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category');
  const navigate = useNavigate();
  const { course, isLoading, error } = useCourse(courseId || '');
  const { user } = useAuth();
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(null);
  const [unlockStatuses, setUnlockStatuses] = useState<LessonUnlockStatus[]>([]);
  const [statusLoading, setStatusLoading] = useState(true);

  // Counter to force refetch on mount (fixes stale unlock status after completing FillIn)
  const [refreshKey, setRefreshKey] = useState(0);
  const [lessonIsRead, setLessonIsRead] = useState(false);

  const isDeutschCourse = courseId === 'deutsch-schularbeit' || courseId === 'mathe-schularbeit';
  const lessonOrder = courseId === 'mathe-schularbeit' ? matheLessonOrder : deutschLessonOrder;

  // Force refetch on every mount
  useEffect(() => {
    setRefreshKey(k => k + 1);
  }, []);

  // Helper: Get fill-in score for a lesson (checks both lesson-linked AND general mode)
  const getFillInScoreForLesson = async (lessonId: string): Promise<number | null> => {
    if (!user?.id || !courseId || !course?.fillInQuestions) return null;

    // Method 1: Check for lesson-specific mode (user accessed via "Jetzt üben!")
    const { data: lessonData } = await supabase
      .from('quiz_results')
      .select('percentage')
      .eq('user_id', user.id)
      .eq('course_id', courseId)
      .eq('mode', `fill-in-${lessonId}`)
      .order('created_at', { ascending: false })
      .limit(1);

    if (lessonData?.[0]?.percentage !== undefined) {
      return lessonData[0].percentage;
    }

    // Method 2: Check general mode results that include this lesson's questions
    // (user accessed via CourseView "Lückentext")
    const { data: generalData } = await supabase
      .from('quiz_results')
      .select('percentage, question_ids')
      .eq('user_id', user.id)
      .eq('course_id', courseId)
      .eq('mode', 'fill-in')
      .order('created_at', { ascending: false });

    if (generalData && generalData.length > 0) {
      // Get question IDs for this lesson
      const lessonQuestionIds = course.fillInQuestions
        .filter(q => 'linkedLesson' in q && q.linkedLesson === lessonId)
        .map(q => q.id);

      // Find a result that covers ALL questions from this lesson
      for (const result of generalData) {
        const answeredIds: string[] = result.question_ids || [];
        const allLessonQuestionsAnswered = lessonQuestionIds.every(id =>
          answeredIds.includes(id)
        );

        if (allLessonQuestionsAnswered && result.percentage !== undefined) {
          return result.percentage;
        }
      }
    }

    // Method 3: Check activity_log as last resort (has percentage in details)
    const { data: activityData } = await supabase
      .from('activity_log')
      .select('details')
      .eq('user_id', user.id)
      .eq('action', 'fill_in')
      .order('created_at', { ascending: false });

    if (activityData) {
      // Find activity with this lesson and good percentage
      const relevantActivity = activityData.find((a: { details: { linked_lesson?: string; percentage?: number } }) =>
        a.details?.linked_lesson === lessonId &&
        a.details?.percentage !== undefined
      );

      if (relevantActivity) {
        return relevantActivity.details.percentage;
      }
    }

    return null;
  };

  // Load unlock statuses for Deutsch course
  useEffect(() => {
    async function loadUnlockStatuses() {
      if (!isDeutschCourse || !user?.id || !course?.textLessons) {
        setStatusLoading(false);
        return;
      }

      // Reset to loading state on refetch
      setStatusLoading(true);

      const statuses: LessonUnlockStatus[] = [];

      for (let i = 0; i < lessonOrder.length; i++) {
        const lessonId = lessonOrder[i];

        // Get fill-in score for this lesson (uses robust multi-method check)
        const score = await getFillInScoreForLesson(lessonId);
        const fillInPassed = score !== null && score >= 60;

        if (i === 0) {
          // First lesson is always unlocked
          statuses.push({
            lessonId,
            isUnlocked: true,
            fillInScore: score,
            fillInPassed,
          });
        } else {
          // Check if previous lesson's fill-in was passed
          const prevStatus = statuses[i - 1];
          const isUnlocked = prevStatus?.fillInPassed ?? false;

          statuses.push({
            lessonId,
            isUnlocked,
            fillInScore: score,
            fillInPassed,
          });
        }
      }

      setUnlockStatuses(statuses);
      setStatusLoading(false);
    }

    loadUnlockStatuses();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeutschCourse, user?.id, courseId, course?.textLessons, course?.fillInQuestions, refreshKey]);

  // Reset read state when lesson changes
  useEffect(() => { setLessonIsRead(false); }, [selectedLessonId]);

  // Log activity and mark lesson as read when opened
  useEffect(() => {
    async function logLessonView() {
      if (!selectedLessonId || !user?.id || !courseId || !course?.textLessons) return;

      const lesson = course.textLessons.find(l => l.id === selectedLessonId);
      if (!lesson) return;

      await logActivity(user.id, 'text_lesson', {
        course_id: courseId,
        lesson_id: lesson.id,
        lesson_title: lesson.title,
      });

      // Note: lesson is marked as read via "Gelesen" button at bottom, not on open
    }

    logLessonView();
  }, [selectedLessonId, user?.id, courseId, course?.textLessons]);

  // Auto-select lesson when category filter is set (only on initial load)
  const [autoSelectDone, setAutoSelectDone] = useState(false);
  useEffect(() => {
    if (categoryFilter && course?.textLessons && course.textLessons.length > 0 && !autoSelectDone) {
      const lesson = course.textLessons.find(l => l.category === categoryFilter);
      if (lesson) setSelectedLessonId(lesson.id);
      setAutoSelectDone(true);
    }
  }, [categoryFilter, course?.textLessons, autoSelectDone]);

  const handleStartExercise = () => {
    if (selectedLessonId) {
      navigate(`/course/${courseId}/fill-in?lesson=${selectedLessonId}`);
    }
  };

  if (isLoading || (isDeutschCourse && statusLoading)) {
    return <div className="loading">Laden...</div>;
  }

  if (error || !course) {
    return (
      <div className="error-state">
        <p>{error || 'Kurs nicht gefunden'}</p>
        <Link to="/" className="back-link">
          <ArrowLeft size={18} />
          Zurück
        </Link>
      </div>
    );
  }

  const textLessons = course.textLessons || [];
  const selectedLesson = textLessons.find(l => l.id === selectedLessonId);
  const currentLessonIndex = textLessons.findIndex(l => l.id === selectedLessonId);
  const currentUnlockStatus = unlockStatuses.find(s => s.lessonId === selectedLessonId);

  // Lesson list view
  if (!selectedLesson) {
    return (
      <div style={{ padding: '16px', paddingBottom: '100px' }}>
        <Link to={`/course/${courseId}`} className="back-link" style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          color: '#9fa8da',
          textDecoration: 'none',
          marginBottom: '20px',
        }}>
          <ArrowLeft size={18} />
          Zurück zum Kurs
        </Link>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px',
        }}>
          <BookOpen size={28} style={{ color: '#ffd54f' }} />
          <h1 style={{
            color: '#fff',
            fontSize: '22px',
            fontWeight: 700,
            margin: 0,
          }}>
            Lernstoff
          </h1>
        </div>

        <p style={{
          color: 'rgba(255, 255, 255, 0.6)',
          fontSize: '14px',
          marginBottom: '20px',
        }}>
          {textLessons.length} Kapitel mit Regeln, Beispielen und Merkhilfen
        </p>

        <div>
          {textLessons.map((lesson) => {
            const unlockStatus = unlockStatuses.find(s => s.lessonId === lesson.id);
            return (
              <LessonCard
                key={lesson.id}
                lesson={lesson}
                onClick={() => setSelectedLessonId(lesson.id)}
                isActive={false}
                unlockStatus={unlockStatus}
                isDeutschCourse={isDeutschCourse}
              />
            );
          })}
        </div>
      </div>
    );
  }

  // Single lesson view
  return (
    <div style={{ padding: '16px', paddingBottom: '100px' }}>
      <button
        onClick={() => setSelectedLessonId(null)}
        className="no-print"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          color: '#9fa8da',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          marginBottom: '20px',
          padding: 0,
          fontSize: '14px',
        }}
      >
        <ArrowLeft size={18} />
        Alle Kapitel
      </button>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '24px',
      }}>
        <span style={{ fontSize: '32px' }}>{selectedLesson.icon}</span>
        <h1 style={{
          color: '#fff',
          fontSize: '22px',
          fontWeight: 700,
          margin: 0,
          flex: 1,
        }}>
          {selectedLesson.title}
        </h1>
        <button
          onClick={() => window.print()}
          className="no-print"
          title="Lektion drucken"
          style={{
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '10px',
            color: '#9fa8da',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Printer size={20} />
        </button>
      </div>

      <div>
        {selectedLesson.sections.map((section, index) => (
          <SectionRenderer key={index} section={section} />
        ))}
      </div>

      {/* Buttons hidden when printing */}
      <div className="no-print">
      {/* "Gelesen" Button to mark lesson as completed */}
      <LessonReadButton
        userId={user?.id}
        courseId={courseId || ''}
        lessonId={selectedLessonId || ''}
        onMarked={() => setRefreshKey(k => k + 1)}
        onReadStateChange={setLessonIsRead}
      />

      {/* "Jetzt üben!" Button — only after lesson is marked as read */}
      {isDeutschCourse && lessonIsRead && (
        <div style={{
          marginTop: '32px',
          paddingTop: '20px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          <button
            onClick={handleStartExercise}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              padding: '18px',
              background: currentUnlockStatus?.fillInPassed
                ? 'rgba(76, 175, 80, 0.2)'
                : 'linear-gradient(135deg, #ffd54f 0%, #ff9800 100%)',
              border: currentUnlockStatus?.fillInPassed
                ? '2px solid #4caf50'
                : 'none',
              borderRadius: '14px',
              color: currentUnlockStatus?.fillInPassed ? '#4caf50' : '#0a0e27',
              fontSize: '17px',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: currentUnlockStatus?.fillInPassed
                ? 'none'
                : '0 4px 15px rgba(255, 152, 0, 0.3)',
            }}
          >
            <PenLine size={22} />
            {currentUnlockStatus?.fillInPassed ? (
              <>
                <CheckCircle size={20} />
                Übung wiederholen
              </>
            ) : (
              'Jetzt üben!'
            )}
          </button>
          {currentUnlockStatus?.fillInPassed && currentUnlockStatus.fillInScore && (
            <p style={{
              textAlign: 'center',
              color: '#4caf50',
              fontSize: '14px',
              marginTop: '8px',
            }}>
              Letztes Ergebnis: {currentUnlockStatus.fillInScore}%
            </p>
          )}
          {!currentUnlockStatus?.fillInPassed && (
            <p style={{
              textAlign: 'center',
              color: 'rgba(255,255,255,0.5)',
              fontSize: '13px',
              marginTop: '8px',
            }}>
              Versuche mindestens 60% zu erreichen!
            </p>
          )}
        </div>
      )}
      {/* Hint: read first to unlock exercises */}
      {isDeutschCourse && !lessonIsRead && (
        <p style={{
          textAlign: 'center',
          color: 'rgba(255,255,255,0.35)',
          fontSize: '13px',
          marginTop: '20px',
        }}>
          Markiere die Lektion als gelesen, um die Übungen freizuschalten.
        </p>
      )}

      {/* Navigation to next/prev lesson */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginTop: isDeutschCourse ? '16px' : '32px',
        paddingTop: isDeutschCourse ? '0' : '20px',
        borderTop: isDeutschCourse ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
      }}>
        {currentLessonIndex > 0 && (
          <button
            onClick={() => {
              setSelectedLessonId(textLessons[currentLessonIndex - 1].id);
              window.scrollTo(0, 0);
            }}
            style={{
              flex: 1,
              padding: '14px',
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '12px',
              color: '#e8eaf6',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Vorherige Lektion
          </button>
        )}
        {currentLessonIndex < textLessons.length - 1 && (
          <button
            onClick={() => {
              setSelectedLessonId(textLessons[currentLessonIndex + 1].id);
              window.scrollTo(0, 0);
            }}
            style={{
              flex: 1,
              padding: '14px',
              background: '#ffd54f',
              border: 'none',
              borderRadius: '12px',
              color: '#0a0e27',
              fontSize: '14px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            Nächste Lektion
          </button>
        )}
      </div>
      </div>{/* end no-print */}
    </div>
  );
}
